import { observer } from "mobx-react-lite";
import { Plus, LayoutGrid } from "lucide-react";
import type { Server } from "@/types";
import { serverStore } from "../../stores/server.store";
import { userStore } from "../../stores/user.store";
import { ServerCard } from "./ServerCard";
import { FundingButton } from "../modals/FundingModal";
import { SystemBar } from "./SystemBar";
import { Button } from "../design/Button";
import { EmptyState } from "../design/EmptyState";
import { cn } from "../../utils/cn";
import { shortcut } from "../../utils/platform";
import { can } from '@/utils/permissions';

const Group = ({ title, dot, items }: { title: string; dot: string; items: Server[] }) =>
  items.length === 0 ? null : (
    <section>
      <div className="flex items-center gap-2 mb-3.5">
        <span className={cn("w-2 h-2 rounded-full", dot)} />
        <h2 className="text-xs font-medium text-fg-muted uppercase tracking-wider">{title}</h2>
        <span className="text-xs text-fg-muted/60 font-mono">{items.length}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((s) => <ServerCard key={s.id} server={s} />)}
      </div>
    </section>
  );

export const ServerBoard = observer(() => {
  const servers = serverStore.getSortedServers();
  const running = servers.filter((s) => s.status === "online");
  const idle = servers.filter((s) => !["online", "archived", "restoring_archive"].includes(s.status));
  const mayCreate = can(userStore.currentUser, "can_create_servers");
  const archived = servers.filter((s) => s.status === "archived" || s.status === "restoring_archive");

  return (
    <div className="p-6 md:p-8 space-y-8 animate-in fade-in duration-300 max-w-[1500px] mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-fg tracking-tight">Deine Server</h1>
          <p className="text-fg-muted text-sm mt-1 flex items-center gap-1.5 flex-wrap">
            Steuerung direkt auf der Karte.
            <span className="hidden sm:inline-flex items-center gap-1 text-fg-muted/80">
              <kbd className="font-mono text-[10px] border border-line rounded px-1.5 py-0.5 bg-white/[0.03]">{shortcut("K")}</kbd> für Schnellzugriff
            </span>
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <FundingButton mode="page" />
          {mayCreate && (
            <Button size="lg" onClick={() => (serverStore.showCreateModal = true)}>
              <Plus size={18} /> Neuer Server
            </Button>
          )}
        </div>
      </header>

      <SystemBar />

      {servers.length === 0 ? (
        <EmptyState
          icon={<LayoutGrid size={22} />}
          title="Noch keine Server"
          description="Leg deinen ersten Server an — geführt in wenigen Schritten, direkt startklar."
          action={mayCreate ? <Button size="lg" onClick={() => (serverStore.showCreateModal = true)}><Plus size={18} /> Server erstellen</Button> : undefined}
        />
      ) : (
        <div className="space-y-8">
          <Group title="Läuft" dot="bg-online" items={running} />
          <Group title="Gestoppt" dot="bg-fg-muted/50" items={idle} />
          <Group title="Archiviert" dot="bg-white/20" items={archived} />
        </div>
      )}
    </div>
  );
});
